/**
 * Transport seam.
 *
 * The typed client speaks to a `Transport`, not to `fetch` directly. In production
 * that is `HttpTransport`; with no API base configured it is the in-memory
 * `MockTransport`, so the built console is always clickable for a demo. Both return
 * the same `{ status, ok, json }` shape, and the client turns non-2xx responses into
 * a typed `ApiError` in exactly one place.
 */
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RawRequest {
  readonly method: HttpMethod;
  /** API-relative path, without the `/v1` prefix or origin, e.g. `/admin/flags`. */
  readonly path: string;
  readonly query?: Readonly<Record<string, string | undefined>>;
  /** JSON body, mutually exclusive with `form`. */
  readonly body?: unknown;
  /** Multipart body for asset uploads. */
  readonly form?: FormData;
  readonly token: string | null;
}

export interface RawResponse {
  readonly status: number;
  readonly ok: boolean;
  /** Parsed response body — a success payload or the `{ error }` envelope. */
  readonly json: unknown;
}

/**
 * A binary response — the media route streams raw bytes (an image or a PDF), not a JSON
 * envelope, so it needs its own read path. On success `blob` carries the bytes and `name`
 * the server-suggested filename (from `Content-Disposition`); on a non-2xx `json` still
 * carries the typed `{ error }` envelope the client maps to an `ApiError`, so error
 * handling is identical to the JSON path.
 */
export interface RawMediaResponse {
  readonly status: number;
  readonly ok: boolean;
  readonly blob: Blob | null;
  readonly name: string | null;
  readonly json: unknown;
}

export interface Transport {
  send(req: RawRequest): Promise<RawResponse>;
  /**
   * Fetch a binary payload (support attachments). Kept separate from `send` so the JSON
   * path never `JSON.parse`s image/PDF bytes — the Wave-1 bug where a chat image blanked
   * because its bytes were run through `JSON.parse`.
   */
  sendMedia(req: RawRequest): Promise<RawMediaResponse>;
}

/** Pull the `filename="…"` out of a `Content-Disposition` header, if present. */
export function filenameFromDisposition(header: string | null): string | null {
  if (header === null) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1] !== undefined) {
    try {
      return decodeURIComponent(star[1].replace(/^["']|["']$/g, ''));
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}
